import { User } from '../types';
import { getAccessToken } from '../../auth/service/storage.service';
import { API_URL } from '../../common/utils';

/**
 * Obtiene la lista de usuarios desde la API
 * @returns Promesa con la lista de usuarios
 */
export const getUsers = async (): Promise<User[]> => {
  try {
    const token = getAccessToken();
    
    if (!token) {
      throw new Error('No se encontró token de autenticación');
    }
    
    const response = await fetch(`${API_URL}/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error al obtener usuarios: ${response.statusText}`);
    }
    
    const data: User[] = await response.json();
    return data;
  } catch (error) {
    console.error('Error al obtener usuarios:', error);
    throw error;
  }
};

/**
 * Crea un nuevo usuario
 * @param userData Datos del usuario a crear (email, password, role)
 * @returns Promesa con el usuario creado
 */
export const createUser = async (userData: { email: string; password: string; role: string }): Promise<User> => {
  try {
    const token = getAccessToken();
    
    if (!token) {
      throw new Error('No se encontró token de autenticación');
    }
    
    const response = await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      throw new Error(`Error al crear usuario: ${response.statusText}`);
    }
    
    const data: User = await response.json();
    return data;
  } catch (error) {
    console.error('Error al crear usuario:', error);
    throw error;
  }
};

/**
 * Actualiza un usuario existente
 * @param id ID del usuario a actualizar
 * @param userData Datos del usuario a actualizar
 * @returns Promesa con el usuario actualizado
 */
export const updateUser = async (id: number, userData: { email?: string; password?: string; role?: string }): Promise<User> => {
  try {
    const token = getAccessToken();
    
    if (!token) {
      throw new Error('No se encontró token de autenticación');
    }
    
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      throw new Error(`Error al actualizar usuario: ${response.statusText}`);
    }
    
    const data: User = await response.json();
    return data;
  } catch (error) {
    console.error('Error al actualizar usuario:', error);
    throw error;
  }
};

/**
 * Elimina un usuario
 * @param id ID del usuario a eliminar
 * @returns Promesa con respuesta de la eliminación
 */
export const deleteUser = async (id: number): Promise<void> => {
  try {
    const token = getAccessToken();
    
    if (!token) {
      throw new Error('No se encontró token de autenticación');
    }
    
    const response = await fetch(`${API_URL}/users/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error al eliminar usuario: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error al eliminar usuario:', error);
    throw error;
  }
}; 